// @AddIntl

import * as dateFnsLocale from 'date-fns/locale'

import {
  CurrencyEnum,
  IntlLocales,
  Iso3166Alpha2Enum,
  TaxIdentifierEnum,
} from './generated/graphql'

export const locales = [
  'de-de',
  'en-gb',
  'es-es',
  'fr-fr',
  'fr-be',
  'it-it',
  'nl-be',
  'nl-nl',
] as const
export type Locale = (typeof locales)[number]

export const defaultLocale: Locale = 'fr-fr'

export type LangParams = {
  langFile: string
  intlLocale: IntlLocales
  countryCode: Iso3166Alpha2Enum
  currency: { symbol: string; currency: CurrencyEnum }
  location: {
    label: string
    placeId: string // https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder
  }
  googleMapLanguage: string
  city: { lat: number; lng: number }
  dateFnsLocale: typeof dateFnsLocale.fr | typeof dateFnsLocale.enGB
  distance: (distance_meters: number) => string
  langLabel: string
  dayJsFormat: {
    format: 'hh:mm A' | 'HH[h]mm'
    locale: 'be' | 'de' | 'en' | 'es' | 'fr' | 'it' | 'nl'
  }
  flag: '🇧🇪' | '🇩🇪' | '🇬🇧' | '🇪🇸' | '🇫🇷' | '🇮🇹' | '🇳🇱'
  landingId: string
}

export const langParams: Record<Locale, LangParams> = {
  'de-de': {
    langFile: 'de-DE',
    intlLocale: IntlLocales.DEDE,
    countryCode: Iso3166Alpha2Enum.DE,
    currency: { symbol: '€', currency: CurrencyEnum.EUR },
    location: { label: 'Deutschland', placeId: 'ChIJa76xwh5ymkcRW-WRjmtd6HU' },
    googleMapLanguage: 'de',
    city: { lat: 52.510885, lng: 13.3989367 }, // Berlin
    dateFnsLocale: dateFnsLocale.de,
    distance: (distance_meters: number) => {
      return new Intl.NumberFormat('de-DE', {
        style: 'unit',
        unit: 'kilometer',
      }).format(Math.round(distance_meters / 1000))
    },
    langLabel: 'Deutsch',
    dayJsFormat: {
      format: 'HH[h]mm',
      locale: 'de',
    },
    flag: '🇩🇪',
    landingId: 'allemagne',
  },
  'en-gb': {
    langFile: 'en-GB',
    intlLocale: IntlLocales.ENGB,
    countryCode: Iso3166Alpha2Enum.GB,
    currency: { symbol: '£', currency: CurrencyEnum.GBP },
    location: {
      label: 'United-Kingdom',
      placeId: 'ChIJ39UebIqp0EcRqI4tMyWV4fQ',
    },
    googleMapLanguage: 'en-GB',
    city: { lat: 51.5076, lng: -0.1278 }, // London
    dateFnsLocale: dateFnsLocale.enGB,
    distance: (distance_meters: number) => {
      return new Intl.NumberFormat('en-GB', {
        style: 'unit',
        unit: 'mile',
      }).format(Math.round(distance_meters / 1609.34))
    },
    langLabel: 'English',
    dayJsFormat: {
      format: 'hh:mm A',
      locale: 'en',
    },
    flag: '🇬🇧',
    landingId: 'england',
  },
  'es-es': {
    langFile: 'es-ES',
    intlLocale: IntlLocales.ESES,
    countryCode: Iso3166Alpha2Enum.ES,
    currency: { symbol: '€', currency: CurrencyEnum.EUR },
    location: { label: 'España', placeId: 'ChIJi7xhMnjjQgwR7KNoB5Qs7KY' },
    googleMapLanguage: 'es',
    city: { lat: 40.4167047, lng: -3.7035825 }, // Madrid
    dateFnsLocale: dateFnsLocale.es,
    distance: (distance_meters: number) => {
      return new Intl.NumberFormat('es-ES', {
        style: 'unit',
        unit: 'kilometer',
      }).format(Math.round(distance_meters / 1000))
    },
    langLabel: 'Español',
    dayJsFormat: {
      format: 'HH[h]mm',
      locale: 'es',
    },
    flag: '🇪🇸',
    landingId: 'espagne',
  },
  'fr-fr': {
    langFile: 'fr-FR',
    intlLocale: IntlLocales.FRFR,
    countryCode: Iso3166Alpha2Enum.FR,
    currency: { symbol: '€', currency: CurrencyEnum.EUR },
    location: { label: 'France', placeId: 'ChIJMVd4MymgVA0R99lHx5Y__Ws' },
    googleMapLanguage: 'fr',
    city: { lat: 48.8566, lng: 2.3522 }, // Paris
    dateFnsLocale: dateFnsLocale.fr,
    distance: (distance_meters: number) => {
      return new Intl.NumberFormat('fr', {
        style: 'unit',
        unit: 'kilometer',
      }).format(Math.round(distance_meters / 1000))
    },
    langLabel: 'Français',
    dayJsFormat: {
      format: 'HH[h]mm',
      locale: 'fr',
    },
    flag: '🇫🇷',
    landingId: 'france',
  },
  'fr-be': {
    langFile: 'fr-BE',
    intlLocale: IntlLocales.FRBE,
    countryCode: Iso3166Alpha2Enum.BE,
    currency: { symbol: '€', currency: CurrencyEnum.EUR },
    location: { label: 'Belgique', placeId: 'ChIJl5fz7WR9wUcR8g_mObTy60c' },
    googleMapLanguage: 'fr',
    city: { lat: 50.8465573, lng: 4.351697 }, // Bruxelles
    dateFnsLocale: dateFnsLocale.fr,
    distance: (distance_meters: number) => {
      return new Intl.NumberFormat('fr', {
        style: 'unit',
        unit: 'kilometer',
      }).format(Math.round(distance_meters / 1000))
    },
    langLabel: 'Français',
    dayJsFormat: {
      format: 'HH[h]mm',
      locale: 'fr',
    },
    flag: '🇧🇪',
    landingId: 'belgique',
  },
  'it-it': {
    langFile: 'it-IT',
    intlLocale: IntlLocales.ITIT,
    countryCode: Iso3166Alpha2Enum.IT,
    currency: { symbol: '€', currency: CurrencyEnum.EUR },
    location: { label: 'Italia', placeId: 'ChIJA9KNRIL-1BIRb15jJFz1LOI' },
    googleMapLanguage: 'it',
    city: { lat: 41.8933203, lng: 12.4829321 }, // Rome
    dateFnsLocale: dateFnsLocale.it,
    distance: (distance_meters: number) => {
      return new Intl.NumberFormat('it-IT', {
        style: 'unit',
        unit: 'kilometer',
      }).format(Math.round(distance_meters / 1000))
    },
    langLabel: 'Italiano',
    dayJsFormat: {
      format: 'HH[h]mm',
      locale: 'it',
    },
    flag: '🇮🇹',
    landingId: 'italie',
  },
  'nl-be': {
    langFile: 'nl-BE',
    intlLocale: IntlLocales.NLBE,
    countryCode: Iso3166Alpha2Enum.BE,
    currency: { symbol: '€', currency: CurrencyEnum.EUR },
    location: { label: 'België', placeId: 'ChIJpeuHbqSSw0cRIIBML6uZAAE' },
    googleMapLanguage: 'nl',
    city: { lat: 51.2085526, lng: 3.226772 }, // Bruges
    dateFnsLocale: dateFnsLocale.nl,
    distance: (distance_meters: number) => {
      return new Intl.NumberFormat('nl', {
        style: 'unit',
        unit: 'kilometer',
      }).format(Math.round(distance_meters / 1000))
    },
    langLabel: 'Nederlands',
    dayJsFormat: {
      format: 'HH[h]mm',
      locale: 'nl',
    },
    flag: '🇧🇪',
    landingId: 'belgique',
  },
  'nl-nl': {
    langFile: 'nl-NL',
    intlLocale: IntlLocales.NLNL,
    countryCode: Iso3166Alpha2Enum.NL,
    currency: { symbol: '€', currency: CurrencyEnum.EUR },
    location: { label: 'Nederland', placeId: 'ChIJu-SH28MJxkcRnwq9_851obM' },
    googleMapLanguage: 'nl',
    city: { lat: 52.3730796, lng: 4.8924534 }, // Amsterdam
    dateFnsLocale: dateFnsLocale.nl,
    distance: (distance_meters: number) => {
      return new Intl.NumberFormat('nl-NL', {
        style: 'unit',
        unit: 'kilometer',
      }).format(Math.round(distance_meters / 1000))
    },
    langLabel: 'Nederlands',
    dayJsFormat: {
      format: 'HH[h]mm',
      locale: 'nl',
    },
    flag: '🇳🇱',
    landingId: 'pays-bas',
  },
}

// Mappers

function getLangParamFromLocale(locale: Locale): LangParams {
  const currentLocale: Locale = isValidLocale(locale) ? locale : defaultLocale

  return langParams[currentLocale]
}

export function getLangFileFromLocale(locale: Locale): LangParams['langFile'] {
  return getLangParamFromLocale(locale).langFile
}

export function getCountryCodeFromLocale(
  locale: Locale,
): LangParams['countryCode'] {
  return getLangParamFromLocale(locale).countryCode
}

export function getCurrencyFromLocale(locale: Locale): LangParams['currency'] {
  return getLangParamFromLocale(locale).currency
}

export const getLocationFromLocale = (
  locale: Locale,
): LangParams['location'] => {
  return getLangParamFromLocale(locale).location
}

export const getGoogleMapLanguageFromLocale = (
  locale: Locale,
): LangParams['googleMapLanguage'] => {
  return getLangParamFromLocale(locale).googleMapLanguage
}

export const getCityFromLocale = (locale: Locale): LangParams['city'] => {
  return getLangParamFromLocale(locale).city
}

export const getDateFnsFromLocale = (
  locale: Locale,
): LangParams['dateFnsLocale'] => {
  return getLangParamFromLocale(locale).dateFnsLocale
}

export const getDistanceFromLocale = (
  locale: Locale,
  distance_meters: number | undefined,
) => {
  if (!distance_meters) {
    return ''
  }

  return getLangParamFromLocale(locale).distance(distance_meters)
}

export const getLangLabelFromLocale = (
  locale: Locale,
): LangParams['langLabel'] => {
  return getLangParamFromLocale(locale).langLabel
}

export const getDayJsFormatFromLocale = (
  locale: Locale,
): LangParams['dayJsFormat'] => {
  return getLangParamFromLocale(locale).dayJsFormat
}

export const getIntlLocaleFromLocale = (
  locale: Locale,
): LangParams['intlLocale'] => {
  return getLangParamFromLocale(locale).intlLocale
}

export const getLangParamFromIntlLocale = (
  intlLocale: IntlLocales,
): LangParams => {
  const langParam = Object.values(langParams).find((langParam) => {
    if (langParam.intlLocale === intlLocale) {
      return true
    }
  })

  return langParam ?? langParams[defaultLocale]
}

export const getLocaleFromIntlLocale = (intlLocale: IntlLocales): Locale => {
  return (Object.keys(langParams).find(
    (locale) => langParams[locale as Locale].intlLocale === intlLocale,
  ) ?? defaultLocale) as Locale
}

// Guards

export function isValidLocale(locale: string | undefined): locale is Locale {
  return locales.includes(locale as Locale)
}

export function getLocale(locale: string | undefined): Locale {
  return isValidLocale(locale) ? locale : defaultLocale
}

// utils

export const getLangFromTaxIdentifier = (
  taxIdentifier?: TaxIdentifierEnum,
): Locale => {
  switch (taxIdentifier) {
    case TaxIdentifierEnum.FRA:
    case TaxIdentifierEnum.BEL:
    case TaxIdentifierEnum.CHE:
    case TaxIdentifierEnum.COR:
      return 'fr-fr'
    case TaxIdentifierEnum.GBR:
    case TaxIdentifierEnum.IRL:
      return 'en-gb'
    default:
      return defaultLocale
  }
}

export const isFrenchLocale = (locale: Locale): boolean => {
  return locale === 'fr-fr'
}

export const getCurrencySymbol = (currency?: CurrencyEnum): string => {
  switch (currency) {
    case CurrencyEnum.CHF:
      return 'CHF'
    case CurrencyEnum.GBP:
      return '£'
    case CurrencyEnum.EUR:
    default:
      return '€'
  }
}

export const getCurrencyFormatterFromCurrency = (
  currency?: CurrencyEnum,
): 'en-GB' | 'fr' => {
  switch (currency) {
    case CurrencyEnum.GBP:
      return 'en-GB'
    default:
      return 'fr'
  }
}
